import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDndScrolling } from 'react-dnd-scrolling';

import { AddContactAttributesModal } from 'components/contact';
import { SidebarToggleTrigger } from 'components/conversation/components/sidebar/sidebarToggleTrigger';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useConversationSidebarSettings } from 'utils/useConversationSidebarSettings';
import { useServerStorage } from 'utils/useServerStorage';

import { SuggestionCards } from './cards/KnowledgeCard';
import { CustomIntegrationCards } from './components/customIntegrationCards/customIntegrationCards';
import { CustomerInfoCards } from './components/customerInfoCards/customerInfoCards';
import { FlatSidebar } from './components/flatSidebar/flatSidebar';
import { IntegrationCards } from './components/integrationCards/integrationCards';
import type {
  SidebarMode,
  SidebarSectionId,
} from './conversationSidebar.types';
import { defaultSectionOrder } from './conversationSidebar.types';
import { useCardSectionVisibility } from './hooks/useCardSectionVisibility';
import { SidebarModeContext } from './hooks/useSidebarMode';

export const ConversationSidebar = (): JSX.Element => {
  const metaConversation = useAppSelector(
    (state) => state.conversation.metaConversation,
  );
  const { hiddenItems } = useCardSectionVisibility();
  const featureFlags = useFeatureFlag([
    'sidebar-cards-admin-management',
    'conversation-right-sidebar-visibility',
  ]);
  // Both flags render the flat, category-less sidebar (reusing the card
  // catalog). The admin-managed flag adds org-level order/visibility/conditions
  // on top; the legacy flag is per-user personalization only. When neither is
  // on we fall back to the old sectioned, non-customizable sidebar.
  const adminManaged = featureFlags.sidebarCardsAdminManagement;
  const legacyCustomize = featureFlags.conversationRightSidebarVisibility;
  const showFlatSidebar = adminManaged || legacyCustomize;
  const orgStorage = useServerStorage('organization');
  // Whether the agent may personalize. Under the admin-managed flag this is the
  // org's setting (defaulting to the legacy flag, matching the settings page);
  // under the legacy flag alone it's that flag directly.
  const canCustomize = adminManaged
    ? (orgStorage.data.conversation_sidebar?.allowAgentPersonalization ??
      legacyCustomize)
    : legacyCustomize;
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('default');

  // Leave customization if personalization is turned off while it's open.
  useEffect(() => {
    if (!canCustomize && sidebarMode === 'customization') {
      setSidebarMode('default');
    }
  }, [canCustomize, sidebarMode]);
  const { cardOpenState, isSidebarOpen, setCardOpen, toggleSidebar } =
    useConversationSidebarSettings();

  const isCardOpen = useCallback(
    (key: string) => cardOpenState[key] ?? false,
    [cardOpenState],
  );

  const toggleCard = useCallback(
    (key: string) => {
      setCardOpen(key, !(cardOpenState[key] ?? false));
    },
    [cardOpenState, setCardOpen],
  );
  const storage = useServerStorage('user');
  const sectionOrder = useMemo(
    () =>
      Array.from(
        new Set([
          ...(storage.data.conversation?.sidebarSectionOrder ?? []),
          ...defaultSectionOrder,
        ]),
      ),
    [storage.data.conversation?.sidebarSectionOrder],
  );

  const rootClassName = classnames('conversation-sidebar-holder', {
    closed: !isSidebarOpen,
  });

  // Auto-scroll the sidebar while dragging a card near its top/bottom edge.
  const rootRef = useRef<HTMLDivElement>(null);
  useDndScrolling(rootRef);

  const renderCardSection = (type: SidebarSectionId, index: number) => {
    switch (type) {
      case 'customerInfo':
        return (
          <CustomerInfoCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
            metaConversation={metaConversation}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
          />
        );

      case 'knowledge':
        return (
          <SuggestionCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
          />
        );
      case 'nativeIntegration':
        return (
          <IntegrationCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
            metaConversation={metaConversation}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
          />
        );
      case 'customIntegration':
        return (
          <CustomIntegrationCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
            metaConversation={metaConversation}
          />
        );
    }
  };

  return (
    <div ref={rootRef} className={rootClassName}>
      <SidebarToggleTrigger
        isMenuOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        sidebarMode={sidebarMode}
        setSidebarMode={setSidebarMode}
        canCustomize={canCustomize}
      />
      <SidebarModeContext.Provider value={sidebarMode}>
        {showFlatSidebar ? (
          <FlatSidebar
            metaConversation={metaConversation}
            isMinimized={!isSidebarOpen}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
          />
        ) : (
          sectionOrder.map((id, index) =>
            sidebarMode === 'customization' || !hiddenItems.has(id)
              ? renderCardSection(id, index)
              : null,
          )
        )}
      </SidebarModeContext.Provider>
      <AddContactAttributesModal />
    </div>
  );
};
